import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

/**
 * Filters kept in the URL's query string, so a link opens the same view.
 *
 * A set of filters is declared once, as a zod object whose every field has a default:
 *
 *   const listFilters = z.object({
 *     status: z.enum(["all", "ready"]).default("all"),
 *     tags: commaSeparated(z.string()).default([]),
 *     ownerId: z.string().nullable().default(null),
 *   });
 *
 * The type of the filters, how a value is read from the URL and how it is written back all come
 * from that declaration. A field's name is its query parameter. A field may be any zod type that
 * reads from a string: an enum, `z.coerce.number()`, `z.stringbool()`, `commaSeparated(...)`.
 */
export type UrlFilterSchema = z.ZodObject<Record<string, z.ZodDefault<z.ZodType>>>;

/**
 * A list kept as comma-separated values (`?tags=a,b`). A value that does not satisfy `item` is
 * dropped, so a stale link loses only the entries that no longer exist.
 */
export function commaSeparated<Value extends string>(item: z.ZodType<Value, Value>) {
  return z.codec(z.string(), z.array(item), {
    decode: (text) =>
      text.split(",").flatMap((value) => {
        const read = item.safeParse(value);
        return read.success ? [read.data] : [];
      }),
    encode: (values) => values.join(","),
  });
}

/**
 * Reads the filters from a URL's search params. A parameter that is missing or invalid gives that
 * filter's default, so no link can break the page.
 */
export function parseUrlFilters<Schema extends UrlFilterSchema>(
  schema: Schema,
  params: URLSearchParams
): z.output<Schema> {
  const filters: Record<string, unknown> = {};
  for (const [name, field] of Object.entries(schema.shape)) {
    const text = params.get(name) ?? undefined;
    const read = field.safeParse(text);
    filters[name] = read.success ? read.data : field.parse(undefined);
  }
  return filters as z.output<Schema>;
}

/**
 * Writes the filters over a URL's search params, leaving other params alone. A filter equal to its
 * default is left out, which keeps links short.
 */
export function withUrlFilters<Schema extends UrlFilterSchema>(
  schema: Schema,
  params: URLSearchParams,
  filters: z.output<Schema>
): URLSearchParams {
  const next = new URLSearchParams(params);
  const written = z.encode(schema, filters) as Record<string, unknown>;
  const defaults = z.encode(schema, parseUrlFilters(schema, new URLSearchParams())) as Record<
    string,
    unknown
  >;
  for (const name of Object.keys(schema.shape)) {
    next.delete(name);
    const value = written[name];
    if (value === null || value === undefined) continue;
    if (JSON.stringify(value) === JSON.stringify(defaults[name])) continue;
    next.set(name, String(value));
  }
  return next;
}

/**
 * The filters in the URL and a setter for them. Changing a filter replaces the history entry
 * rather than adding one, so Back leaves the page instead of stepping through each choice.
 */
export function useUrlFilters<Schema extends UrlFilterSchema>(
  schema: Schema
): [z.output<Schema>, (filters: z.output<Schema>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseUrlFilters(schema, searchParams), [schema, searchParams]);
  const setFilters = useCallback(
    (next: z.output<Schema>) =>
      setSearchParams((current) => withUrlFilters(schema, current, next), { replace: true }),
    [schema, setSearchParams]
  );
  return [filters, setFilters];
}
